// **The main-thread half of the demo stage worker** (`demo-presentation-shell` task 5.7, design
// **DD3**). `demo.worker.ts` is the device-side composition root; this is the adapter that makes
// one of them satisfy the {@link DemoStageDriver} seam the sequencer already drives, plus the
// snapshot subscription the stage's render loop consumes.
//
// It owns the thread's lifecycle, the `visibilitychange` pause, and nothing else. Every rule about
// stepping, catch-up, batching and cancellation lives on the other side, where `stage-clock.ts`
// tests it without a browser.
//
// **Every op resolves on the worker's completion event, never on the post.** `postMessage` returns
// when the message is *queued*: it says nothing about whether the world accepted the op, and
// resolving there was wrong in three separate ways.
//
//   * A **refused hot-swap read as a success** — the type editor cleared its diagnostics and
//     recorded a draft the compiler had rejected as the last-good program, while the world kept
//     running the previous one. So `hotSwap` and `createType` **reject**, carrying the compiler's
//     diagnostics; every other op resolves even on a refusal, because the show advances past one.
//   * A **fast-forward resolved before its batches finished**, so a stage reset or chapter jump
//     awaiting it configured a world the fast-forward then went on advancing.
//   * A **spawn could reach a worker still loading its wasm module** and be dropped silently —
//     which is why `configure` already waited, and why waiting is now the rule rather than the
//     exception.
//
// The two **reads** (`vocabulary`, `worldForInstruct`) cross the same way. Without them production
// showed an empty vocabulary modal and posted the instruct endpoint a synthetic empty world, while
// the direct test driver — implementing both — reported everything working.

import type {
  DemoBeeCommandOp,
  DemoCommandOp,
  DemoHotSwapOp,
  DemoInstructWorldRead,
  DemoSpawnOp,
  DemoStageDriver,
  DemoStageSpec,
  DemoVocabEntry,
} from './stage-driver';
import { practiceWasmGlueUrl } from '../rounds/practice-wasm';
import type { DemoCreateTypeOp, DemoWorkerEvent, DemoWorkerRequest } from './demo-worker-protocol';

/** The thread, narrowed to what this file uses — a real `Worker` satisfies it structurally, and
 * `test/fake-demo-worker-channel.ts`'s host port does too (jsdom has no `Worker` at all). */
export interface DemoWorkerPort {
  postMessage(message: DemoWorkerRequest): void;
  addEventListener(type: 'message', listener: (event: { data: unknown }) => void): void;
  addEventListener(type: 'error', listener: (event: unknown) => void): void;
  addEventListener(type: 'messageerror', listener: (event: unknown) => void): void;
  terminate?(): void;
}

/** The document-visibility seam. Injected so the pause rule is drivable without a real tab. */
export interface VisibilitySource {
  hidden(): boolean;
  subscribe(listener: () => void): () => void;
}

export function browserVisibility(): VisibilitySource {
  return {
    hidden: () => document.visibilityState === 'hidden',
    subscribe(listener) {
      document.addEventListener('visibilitychange', listener);
      return () => document.removeEventListener('visibilitychange', listener);
    },
  };
}

export interface DemoWorkerHostDeps {
  /** Spawns the thread. Defaults to the real module worker. May throw on a device with no
   * module-worker support — reported as `unavailable`, never thrown past the composition root. */
  spawn?: () => DemoWorkerPort;
  glueUrl?: string;
  visibility?: VisibilitySource;
  /** Every render snapshot the worker posts, as C-1's own JSON — parsed by the adapter on this
   * thread (`stage-snapshot.ts`), never here. */
  onSnapshot?: (json: string) => void;
  /** A named C-1 refusal, or a device that cannot run the stage. Reported, never thrown: the show
   * advances past a refusal. */
  onRefused?: (event: Extract<DemoWorkerEvent, { kind: 'refused' | 'unavailable' }>) => void;
  onConfigured?: (event: Extract<DemoWorkerEvent, { kind: 'configured' }>) => void;
}

export interface DemoStageHost extends DemoStageDriver {
  createType(op: DemoCreateTypeOp): Promise<void>;
  /** Stops a running fast-forward (**DD12**'s ≤ 1 s failsafe). */
  cancel(): void;
  /** Releases the thread. Idempotent. */
  close(): void;
}

/** What a settled-by-loss read answers with: an empty vocabulary, and a world with nothing in it.
 * A thread that has died has no roster to report, and the surfaces above degrade rather than
 * throw — but a *live* thread now answers for real, which is the point. */
const LOST_WORLD: DemoInstructWorldRead = {
  fieldSizeU: 0,
  hive: { x: 0, y: 0 },
  roster: [],
  patches: [],
};

/** One in-flight request, awaiting its `completed`/`refused`. */
interface Pending {
  settle(value: unknown): void;
  fail(error: Error): void;
  /** Whether a named refusal rejects the caller (the two compile paths) or merely settles it. */
  rejectsOnRefusal: boolean;
  /** What to settle with if the thread dies or the host closes before an answer arrives. */
  lost: unknown;
}

/** The presenter-readable text of a refusal: the compiler's own diagnostics where there are any,
 * the machine reason code otherwise. `compile_failed` alone tells the editor nothing to show. */
function refusalText(event: Extract<DemoWorkerEvent, { kind: 'refused' }>): string {
  return event.diagnostics.map((diagnostic) => diagnostic.message).join('; ') || event.reason;
}

/** The real thread. The URL literal must stay inline — Vite detects this exact form at build time
 * and would not emit the worker chunk for a URL held in a variable. */
function spawnModuleWorker(): DemoWorkerPort {
  return new Worker(new URL('./demo.worker.ts', import.meta.url), {
    type: 'module',
  }) as unknown as DemoWorkerPort;
}

export function createDemoWorkerHost(deps: DemoWorkerHostDeps = {}): DemoStageHost {
  const visibility = deps.visibility ?? browserVisibility();
  let port: DemoWorkerPort | null = null;
  let closed = false;
  let unsubscribeVisibility: (() => void) | null = null;
  /** Every request awaiting its terminal event, by id. */
  const pending = new Map<number, Pending>();
  let nextId = 1;

  function take(id: number): Pending | undefined {
    const entry = pending.get(id);
    pending.delete(id);
    return entry;
  }

  /** Settles every outstanding request with its loss value — a dead thread, or a close. Never
   * rejects: a hung sequencer in front of an audience is worse than a stage that stopped. */
  function loseAll(): void {
    const entries = [...pending.values()];
    pending.clear();
    for (const entry of entries) entry.settle(entry.lost);
  }

  /** The last rate {@link DemoStageHost.setRate} was asked for, or `null` if it never was. Applied
   * to a freshly spawned port so a request made before the thread existed is not lost — see
   * `setRate`'s own note for why losing it was a visible defect rather than a no-op. */
  let pendingRate: number | null = null;

  function ensurePort(): DemoWorkerPort | null {
    if (closed) return null;
    if (port !== null) return port;
    try {
      port = (deps.spawn ?? spawnModuleWorker)();
    } catch (error) {
      deps.onRefused?.({ kind: 'unavailable', detail: String(error) });
      closed = true;
      return null;
    }
    port.addEventListener('message', (event: { data: unknown }) => {
      const message = event.data as DemoWorkerEvent;
      switch (message?.kind) {
        case 'snapshot':
          deps.onSnapshot?.(message.json);
          return;
        case 'configured':
          // The world's facts. Not a settlement — the `completed` that follows is.
          deps.onConfigured?.(message);
          return;
        case 'completed':
          take(message.id)?.settle(undefined);
          return;
        case 'vocabularyRead':
          take(message.id)?.settle(message.entries);
          return;
        case 'worldRead':
          take(message.id)?.settle(message.world);
          return;
        case 'refused': {
          deps.onRefused?.(message);
          if (message.id === null) return; // a stepped tick's refusal — nobody is waiting on it
          const entry = take(message.id);
          if (entry === undefined) return;
          if (entry.rejectsOnRefusal) entry.fail(new Error(refusalText(message)));
          else entry.settle(entry.lost);
          return;
        }
        case 'unavailable':
          deps.onRefused?.(message);
          // Nothing on that thread will answer now, so nothing may keep waiting for it.
          loseAll();
          return;
        default:
          return;
      }
    });
    // A thread that dies is contained: the show carries on with a frozen stage rather than a
    // hung sequencer.
    const died = (): void => {
      deps.onRefused?.({ kind: 'unavailable', detail: 'the demo stage worker stopped' });
      loseAll();
    };
    port.addEventListener('error', died);
    port.addEventListener('messageerror', died);

    unsubscribeVisibility = visibility.subscribe(() => {
      port?.postMessage({ kind: visibility.hidden() ? 'pause' : 'resume' });
    });
    // A rate asked for before this thread existed. Posted now, so the world it is about to run
    // starts at the rate the presenter has already selected rather than at the clock's default.
    if (pendingRate !== null) {
      port.postMessage({ kind: 'setRate', rate: pendingRate });
    }
    return port;
  }

  /**
   * Posts a correlated request and resolves when the worker says it finished it.
   *
   * `lost` is what the promise settles with when there is no thread to answer — a closed host, a
   * failed spawn, a dead worker — so every caller degrades to the same silent no-op the
   * `AmbientBackground` precedent establishes rather than hanging or throwing.
   */
  function request<T>(
    build: (id: number) => DemoWorkerRequest,
    lost: T,
    rejectsOnRefusal = false,
  ): Promise<T> {
    const target = ensurePort();
    if (target === null) return Promise.resolve(lost);
    const id = nextId++;
    return new Promise<T>((resolve, reject) => {
      pending.set(id, {
        settle: (value) => resolve(value as T),
        fail: reject,
        rejectsOnRefusal,
        lost,
      });
      target.postMessage(build(id));
    });
  }

  return {
    configure: (stage: DemoStageSpec) =>
      request<void>(
        (id) => ({ kind: 'configure', id, stage, glueUrl: deps.glueUrl ?? practiceWasmGlueUrl() }),
        undefined,
      ),
    spawn: (op: DemoSpawnOp) => request<void>((id) => ({ kind: 'spawn', id, op }), undefined),
    // The two compile paths, and the only two that reject: the type editor and the Beekeeper
    // chapter both have diagnostics to put on screen and a draft that must not be recorded as the
    // running program.
    hotSwap: (op: DemoHotSwapOp) => request<void>((id) => ({ kind: 'hotSwap', id, op }), undefined, true),
    createType: (op: DemoCreateTypeOp) =>
      request<void>((id) => ({ kind: 'createType', id, op }), undefined, true),
    command: (op: DemoCommandOp) => request<void>((id) => ({ kind: 'command', id, op }), undefined),
    submitCommand: (op: DemoBeeCommandOp) =>
      request<void>((id) => ({ kind: 'submitCommand', id, op }), undefined),
    fastForward: (ticks: number) =>
      request<void>((id) => ({ kind: 'fastForward', id, ticks }), undefined),
    vocabulary: () =>
      request<readonly DemoVocabEntry[]>((id) => ({ kind: 'read', id, what: 'vocabulary' }), []),
    worldForInstruct: () =>
      request<DemoInstructWorldRead>(
        (id) => ({ kind: 'read', id, what: 'worldForInstruct' }),
        LOST_WORLD,
      ),
    /**
     * Fire-and-forget, exactly like `cancel`/the visibility pause below: there is no completion
     * event to await, so this never spawns a thread just to tell one about a rate.
     *
     * **But the rate is remembered, and that is the point.** A press before the first `configure`
     * has no port to post into. Dropping it silently was not harmless: `DemoApp` had already
     * advanced its own idea of the rate, so the presenter's lever and the world's actual rate sat
     * one cycle position apart for the rest of the show — the next press would post `1` while they
     * expected `4`, halving the world below its own default in front of the audience with no
     * on-screen indicator to explain it. Latching the last requested rate here and applying it when
     * the port does spawn keeps the two in step without making the app track port lifetime.
     */
    setRate(rate: number): void {
      pendingRate = rate;
      port?.postMessage({ kind: 'setRate', rate });
    },
    cancel(): void {
      port?.postMessage({ kind: 'cancel' });
    },
    close(): void {
      if (closed) return;
      closed = true;
      unsubscribeVisibility?.();
      unsubscribeVisibility = null;
      loseAll();
      port?.postMessage({ kind: 'close' });
      port?.terminate?.();
      port = null;
    },
  };
}
